import { useState } from 'react'
import { TIME_BUCKETS } from './chartTypes'
import InlineCkEditor from 'Shared/components/CKEditor/InlineCkEditor'
import type { FactTable, Metric, Dimension, ColumnMetric, CustomMetricRef, RatioMetric } from 'AnalyticsDashboard/utils/factTableHelpers'
import {
  generateMetricId,
  metricDisplayLabel,
  expandQuickMetric,
  isColumnMetric,
  isCustomMetricRef,
  isRatioMetric,
  OPERATION_LABELS,
} from 'AnalyticsDashboard/utils/factTableHelpers'

declare const _: any

interface SlotDef {
  label: string
  required: boolean
  kind: string
  single?: boolean
  includeTime?: boolean
}

interface QueryInfo {
  availableDimensions: Dimension[]
  availableFilters: any[]
}

interface SlotEditorProps {
  slot: SlotDef
  slotKey: string
  value: any
  allSlotValues: Record<string, any>
  allSlots: Record<string, SlotDef>
  onChange: (value: any) => void
  queryInfo: QueryInfo | null
  factTables: FactTable[]
}

// Collect dimension IDs used by other dimension slots (to exclude from picker)
function usedDimIds(allSlots: Record<string, SlotDef>, allSlotValues: Record<string, any>, currentSlotKey: string): string[] {
  const ids: string[] = []
  for (const [key, s] of Object.entries(allSlots)) {
    if (key === currentSlotKey || s.kind !== 'dimension') continue
    const val = allSlotValues[key]
    if (Array.isArray(val)) {
      val.forEach((d: any) => ids.push(d.id || d))
    } else if (val?.id) {
      ids.push(val.id)
    }
  }
  return ids
}

export function SlotEditor({ slot, slotKey, value, allSlotValues, allSlots, onChange, queryInfo, factTables }: SlotEditorProps) {
  if (slot.kind === 'html') {
    return <HtmlSlot slot={slot} value={value} onChange={onChange} />
  }
  if (slot.kind === 'time') {
    return <TimeDimensionSlot slot={slot} value={value} onChange={onChange} queryInfo={queryInfo} />
  }
  if (slot.kind === 'metric') {
    const selectedMetrics: Metric[] = Array.isArray(value) ? value : value ? [value] : []
    return (
      <MetricPicker
        label={slot.label}
        required={slot.required}
        single={!!slot.single}
        factTables={factTables}
        selectedMetrics={selectedMetrics}
        onChange={onChange}
      />
    )
  }
  if (slot.kind === 'boolean') {
    return (
      <div className="query-builder__section">
        <label className="query-builder__checkbox">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          {slot.label}
        </label>
      </div>
    )
  }
  if (slot.kind === 'geoDimension') {
    const geoDims = queryInfo?.availableDimensions.filter(d => d.geo) || []
    return (
      <div className="query-builder__section">
        <h3>{slot.label}</h3>
        <div className="query-builder__options">
          {geoDims.map(d => (
            <button
              key={d.id}
              type="button"
              className={`query-builder__option ${value?.id === d.id ? 'active' : ''}`}
              onClick={() => onChange(value?.id === d.id ? null : { id: d.id })}
            >
              {d.name || d.label || d.id}
            </button>
          ))}
          {geoDims.length === 0 && (
            <span style={{ color: '#94a3b8', fontSize: 13 }}>No geographic dimensions available for this data source</span>
          )}
        </div>
      </div>
    )
  }
  if (slot.kind === 'dimension') {
    const includeTime = !!slot.includeTime
    const excludeIds = usedDimIds(allSlots, allSlotValues, slotKey)
    const allDims = queryInfo?.availableDimensions || []
    let dims = includeTime ? allDims : allDims.filter(d => d.kind !== 'time')
    dims = dims.filter(d => !excludeIds.includes(d.id))
    return (
      <DimensionPicker
        label={slot.label}
        required={slot.required}
        single={!!slot.single}
        availableDimensions={dims}
        selectedDimensions={Array.isArray(value) ? value : value ? [value] : []}
        onToggle={(dimId: string) => {
          const dimInfo = allDims.find(d => d.id === dimId)
          const newDim: { id: string; timeBucket?: string } = { id: dimId }
          if (dimInfo?.kind === 'time') newDim.timeBucket = 'day'

          if (slot.single) {
            onChange(value?.id === dimId ? null : newDim)
          } else {
            const arr = Array.isArray(value) ? value : []
            const exists = arr.find((d: any) => d.id === dimId)
            onChange(exists ? arr.filter((d: any) => d.id !== dimId) : [...arr, newDim])
          }
        }}
        onUpdateTimeBucket={(dimId: string, bucket: string) => {
          if (slot.single) {
            onChange({ ...value, timeBucket: bucket })
          } else {
            const arr = Array.isArray(value) ? value : []
            onChange(arr.map((d: any) => d.id === dimId ? { ...d, timeBucket: bucket } : d))
          }
        }}
      />
    )
  }
  return null
}

// --- Metric Picker (composable: fact table → column → operation) ---

interface MetricPickerProps {
  label: string
  required: boolean
  single: boolean
  factTables: FactTable[]
  selectedMetrics: Metric[]
  onChange: (metrics: Metric[]) => void
}

function MetricPicker({ label, required, single, factTables, selectedMetrics, onChange }: MetricPickerProps) {
  const [showBuilder, setShowBuilder] = useState(selectedMetrics.length === 0)
  const [editingId, setEditingId] = useState<string | null>(null)
  const canAdd = single ? selectedMetrics.length === 0 : true

  const addMetric = (metric: Metric) => {
    const withId = { ...metric, id: generateMetricId(selectedMetrics) }
    onChange(single ? [withId] : [...selectedMetrics, withId])
    setShowBuilder(false)
  }

  const updateMetric = (metric: Metric) => {
    onChange(selectedMetrics.map(m => m.id === editingId ? { ...metric, id: editingId } : m))
    setEditingId(null)
    setShowBuilder(false)
  }

  const editMetric = (metricId: string) => {
    setEditingId(metricId)
    setShowBuilder(true)
  }

  const removeMetric = (metricId: string) => {
    const next = selectedMetrics.filter(m => m.id !== metricId)
    onChange(next)
    if (editingId === metricId) { setEditingId(null) }
    if (next.length === 0) setShowBuilder(true)
  }

  const closeBuilder = () => {
    setShowBuilder(false)
    setEditingId(null)
  }

  const editingMetric = editingId ? selectedMetrics.find(m => m.id === editingId) || null : null

  return (
    <div className="query-builder__section">
      <h3>{label}{!required && <span className="query-builder__optional"> (optional)</span>}</h3>
      {selectedMetrics.length > 0 && (
        <div className="query-builder__pills">
          {selectedMetrics.map(metric => (
            <span
              key={metric.id}
              className={`query-builder__pill ${editingId === metric.id ? 'active' : ''}`}
              onClick={() => editMetric(metric.id)}
              style={{ cursor: 'pointer' }}
            >
              {metricDisplayLabel(metric, factTables)}
              <button type="button" onClick={(e) => { e.stopPropagation(); removeMetric(metric.id) }}>&times;</button>
            </span>
          ))}
          {canAdd && !showBuilder && (
            <button type="button" className="query-builder__pill-add" onClick={() => { setEditingId(null); setShowBuilder(true) }}>
              + Add metric
            </button>
          )}
        </div>
      )}
      {showBuilder && (
        <MetricBuilder
          key={editingId || 'new'}
          factTables={factTables}
          existingMetrics={selectedMetrics}
          editingMetric={editingMetric}
          onAdd={editingId ? updateMetric : addMetric}
          onQuickAdd={addMetric}
          onClose={selectedMetrics.length > 0 ? closeBuilder : undefined}
        />
      )}
    </div>
  )
}

interface MetricBuilderProps {
  factTables: FactTable[]
  existingMetrics: Metric[]
  editingMetric?: Metric | null
  onAdd: (metric: Metric) => void
  onQuickAdd: (metric: Metric) => void
  onClose?: () => void
}

function initFromMetric(metric: Metric | null | undefined) {
  if (!metric) return { factTable: '', column: '', operation: '', value: '', customMetric: '', numerator: '', denominator: '' }
  if (isRatioMetric(metric)) return { factTable: '_ratio', column: '', operation: '', value: '', customMetric: '', numerator: metric.numerator, denominator: metric.denominator }
  if (isCustomMetricRef(metric)) return { factTable: metric.factTable, column: '', operation: '', value: '', customMetric: metric.customMetric, numerator: '', denominator: '' }
  if (isColumnMetric(metric)) return { factTable: metric.factTable, column: metric.column, operation: metric.operation, value: metric.value || '', customMetric: '', numerator: '', denominator: '' }
  return { factTable: '', column: '', operation: '', value: '', customMetric: '', numerator: '', denominator: '' }
}

function MetricBuilder({ factTables, existingMetrics, editingMetric, onAdd, onQuickAdd, onClose }: MetricBuilderProps) {
  const init = initFromMetric(editingMetric)
  const [factTable, setFactTable] = useState(init.factTable)
  const [column, setColumn] = useState(init.column)
  const [operation, setOperation] = useState(init.operation)
  const [value, setValue] = useState(init.value)
  const [customMetric, setCustomMetric] = useState(init.customMetric)
  const [numerator, setNumerator] = useState(init.numerator)
  const [denominator, setDenominator] = useState(init.denominator)

  const selectedFt = factTables.find(ft => ft.kind === factTable)
  const selectedCol = selectedFt?.columns?.find(c => c.name === column)

  const columnValid = factTable && column && operation && (operation !== 'count_where' || value)
  const customValid = factTable && customMetric
  const ratioValid = numerator && denominator && numerator !== denominator

  const handleAddColumn = () => {
    if (!columnValid) return
    const metric: ColumnMetric = { id: '', factTable, column, operation }
    if (operation === 'count_where' && value) metric.value = value
    onAdd(metric)
  }

  const handleAddCustom = () => {
    if (!customValid) return
    onAdd({ id: '', factTable, customMetric } as CustomMetricRef)
  }

  const handleAddRatio = () => {
    if (!ratioValid) return
    onAdd({ id: '', operation: 'ratio', numerator, denominator } as RatioMetric)
  }

  return (
    <div className="query-builder__picker-list">
      <div className="query-builder__metric-browser">
        <div className="query-builder__metric-browser-nav">
          {factTables.map(ft => (
            <button
              key={ft.kind}
              type="button"
              className={`query-builder__metric-browser-nav-item ${factTable === ft.kind ? 'active' : ''}`}
              onClick={() => { setFactTable(ft.kind); setColumn(''); setOperation(''); setValue(''); setCustomMetric('') }}
            >
              {ft.label}
            </button>
          ))}
          {existingMetrics.length >= 2 && (
            <button
              type="button"
              className={`query-builder__metric-browser-nav-item ${factTable === '_ratio' ? 'active' : ''}`}
              onClick={() => setFactTable('_ratio')}
            >
              Ratio
            </button>
          )}
        </div>

        <div className="query-builder__metric-browser-detail">
          {!selectedFt && factTable !== '_ratio' && (
            <span style={{ color: '#94a3b8', fontSize: 13 }}>Select a data source</span>
          )}

          {selectedFt && (
            <>
              {selectedFt.quickMetrics && selectedFt.quickMetrics.length > 0 && (
                <>
                  <h4>Quick add</h4>
                  <div className="query-builder__options">
                    {selectedFt.quickMetrics.map(qm => (
                      <button
                        key={qm.id}
                        type="button"
                        className="query-builder__picker-item"
                        onClick={() => onQuickAdd(expandQuickMetric(qm, factTable))}
                      >
                        {qm.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <h4>Columns</h4>
              <div className="query-builder__picker-steps">
                <select
                  value={column}
                  onChange={e => { setColumn(e.target.value); setOperation(''); setValue('') }}
                >
                  <option value="">Select column</option>
                  {selectedFt.columns.map(c => (
                    <option key={c.name} value={c.name}>{c.label || c.name}</option>
                  ))}
                </select>

                {selectedCol && (
                  <select
                    value={operation}
                    onChange={e => { setOperation(e.target.value); setValue('') }}
                  >
                    <option value="">Select operation</option>
                    {selectedCol.operations.map(op => (
                      <option key={op} value={op}>{OPERATION_LABELS[op] || op}</option>
                    ))}
                  </select>
                )}

                {operation === 'count_where' && selectedCol?.knownValues && (
                  <select value={value} onChange={e => setValue(e.target.value)}>
                    <option value="">Select value</option>
                    {selectedCol.knownValues.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                )}
                {operation === 'count_where' && !selectedCol?.knownValues && (
                  <input
                    type="text"
                    placeholder="Value"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                  />
                )}

                {columnValid && (
                  <button type="button" className="query-builder__picker-item" onClick={handleAddColumn}>{editingMetric ? 'Update' : 'Add'}</button>
                )}
              </div>

              {selectedFt.customMetrics?.length > 0 && (
                <>
                  <h4>Custom metrics</h4>
                  <div className="query-builder__picker-steps">
                    <select value={customMetric} onChange={e => setCustomMetric(e.target.value)}>
                      <option value="">Select metric</option>
                      {selectedFt.customMetrics.map(cm => (
                        <option key={cm.id} value={cm.id}>{cm.label || cm.id}</option>
                      ))}
                    </select>

                    {customValid && (
                      <button type="button" className="query-builder__picker-item" onClick={handleAddCustom}>{editingMetric ? 'Update' : 'Add'}</button>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {factTable === '_ratio' && (
            <div className="query-builder__picker-steps">
              <select value={numerator} onChange={e => setNumerator(e.target.value)}>
                <option value="">Numerator</option>
                {existingMetrics.map(m => (
                  <option key={m.id} value={m.id}>{metricDisplayLabel(m, factTables)}</option>
                ))}
              </select>

              <span style={{ padding: '0 8px', color: '#94a3b8' }}>/</span>

              <select value={denominator} onChange={e => setDenominator(e.target.value)}>
                <option value="">Denominator</option>
                {existingMetrics.map(m => (
                  <option key={m.id} value={m.id}>{metricDisplayLabel(m, factTables)}</option>
                ))}
              </select>

              {ratioValid && (
                <button type="button" className="query-builder__picker-item" onClick={handleAddRatio}>{editingMetric ? 'Update' : 'Add'}</button>
              )}
            </div>
          )}
        </div>
      </div>

      {onClose && (
        <div className="query-builder__picker-actions">
          <button type="button" className="query-builder__picker-close" onClick={onClose}>
            Done
          </button>
        </div>
      )}
    </div>
  )
}

// --- Time Dimension Slot ---

interface TimeDimensionSlotProps {
  slot: SlotDef
  value: any
  onChange: (value: any) => void
  queryInfo: QueryInfo | null
}

function TimeDimensionSlot({ slot, value, onChange, queryInfo }: TimeDimensionSlotProps) {
  const timeDimensions = queryInfo?.availableDimensions.filter(d => d.kind === 'time') || []

  if (timeDimensions.length === 0) return null

  const selected = value || (slot.required ? { id: timeDimensions[0].id, timeBucket: 'day' } : null)
  const dimInfo = selected && timeDimensions.find(d => d.id === selected.id)

  return (
    <div className="query-builder__section">
      <h3>{slot.label}{!slot.required && <span className="query-builder__optional"> (optional)</span>}</h3>
      <div className="query-builder__pills">
        {!selected && (
          <button
            type="button"
            className="query-builder__pill-add"
            onClick={() => onChange({ id: timeDimensions[0].id, timeBucket: 'day' })}
          >
            + Add time dimension
          </button>
        )}
        {selected && (
          <span className="query-builder__pill">
            {dimInfo?.name || dimInfo?.label || selected.id}
            <select
              className="query-builder__pill-select"
              value={selected.timeBucket || 'day'}
              onChange={(e) => onChange({ ...selected, timeBucket: e.target.value })}
            >
              {TIME_BUCKETS.map(b => (
                <option key={b} value={b}>{_.capitalize(b)}</option>
              ))}
            </select>
            {!slot.required && (
              <button type="button" onClick={() => onChange(null)}>&times;</button>
            )}
          </span>
        )}
        {selected && timeDimensions.length > 1 && (
          <select
            className="query-builder__pill-select"
            value={selected.id}
            onChange={(e) => onChange({ id: e.target.value, timeBucket: selected.timeBucket || 'day' })}
          >
            {timeDimensions.map(d => (
              <option key={d.id} value={d.id}>{d.name || d.label || d.id}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  )
}

// --- Dimension Picker ---

function groupDimensions(availableDimensions: Dimension[]) {
  const byGroup: Record<string, Dimension[]> = {}
  availableDimensions.forEach(d => {
    const group = d.group || 'fields'
    if (!byGroup[group]) byGroup[group] = []
    byGroup[group].push(d)
  })
  const groupOrder = Object.keys(byGroup).sort((a, b) => {
    if (a === 'fields') return -1
    if (b === 'fields') return 1
    return a.localeCompare(b)
  })
  return { byGroup, groupOrder }
}

function dimLabel(d: Dimension): string {
  return d.name || d.label || d.id
}

interface DimensionPickerProps {
  label: string
  required: boolean
  single: boolean
  availableDimensions: Dimension[]
  selectedDimensions: Array<{ id: string; timeBucket?: string }>
  onToggle: (dimId: string) => void
  onUpdateTimeBucket: (dimId: string, bucket: string) => void
}

function DimensionPicker({ label, required, single, availableDimensions, selectedDimensions, onToggle, onUpdateTimeBucket }: DimensionPickerProps) {
  const [showList, setShowList] = useState(false)
  const selectedIds = selectedDimensions.map(d => d.id || d)
  const unselected = availableDimensions.filter(d => !selectedIds.includes(d.id))
  const { byGroup, groupOrder } = groupDimensions(unselected)
  const canAdd = single ? selectedDimensions.length === 0 : unselected.length > 0

  return (
    <div className="query-builder__section">
      <h3>{label}{!required && <span className="query-builder__optional"> (optional)</span>}</h3>
      <div className="query-builder__pills">
        {selectedDimensions.map(sel => {
          const dimId = sel.id || (sel as any)
          const info = availableDimensions.find(d => d.id === dimId)
          return (
            <span key={dimId} className="query-builder__pill">
              {dimLabel(info || { id: dimId, kind: '' })}
              {info?.kind === 'time' && sel.timeBucket && (
                <select
                  className="query-builder__pill-select"
                  value={sel.timeBucket}
                  onChange={(e) => onUpdateTimeBucket(dimId, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {TIME_BUCKETS.map(b => (
                    <option key={b} value={b}>{_.capitalize(b)}</option>
                  ))}
                </select>
              )}
              <button type="button" onClick={() => onToggle(dimId)}>&times;</button>
            </span>
          )
        })}
        {canAdd && !showList && (
          <button type="button" className="query-builder__pill-add" onClick={() => setShowList(true)}>
            + Add dimension
          </button>
        )}
      </div>
      {showList && (
        <div className="query-builder__picker-list">
          {groupOrder.map(group => (
            <div key={group} className="query-builder__picker-group">
              <span className="query-builder__picker-group-label">{_.capitalize(group)}</span>
              {byGroup[group].map(d => (
                <button
                  key={d.id}
                  type="button"
                  className="query-builder__picker-item"
                  onClick={() => { onToggle(d.id); if (single) setShowList(false) }}
                >
                  {dimLabel(d)}
                </button>
              ))}
            </div>
          ))}
          <button type="button" className="query-builder__picker-close" onClick={() => setShowList(false)}>
            Done
          </button>
        </div>
      )}
    </div>
  )
}

// --- HTML Slot ---

interface HtmlSlotProps {
  slot: SlotDef
  value: any
  onChange: (value: any) => void
}

function HtmlSlot({ slot, value, onChange }: HtmlSlotProps) {
  const [sourceMode, setSourceMode] = useState(false)

  return (
    <div className="query-builder__section">
      <h3>{slot.label}</h3>
      <InlineCkEditor
        value={value || ''}
        onUpdate={onChange}
        sourceMode={sourceMode}
        setSourceMode={setSourceMode}
        hideMergeTags
        features={['sourceMode']}
        placeholder="Type content here..."
      />
    </div>
  )
}
